// Из чего складывается одна сумма: полоса долей, вывод словами и таблица под ней.
//
// ## Зачем компонент, а не разметка на каждом экране
//
// Один и тот же вопрос — «на что ушёл миллион», «из чего сложились полтора миллиона долга»,
// «что мы потеряли безвозвратно» — до 08.09.2026 рисовался на четырёх экранах четырьмя
// способами: где-то список с одинокой полоской у каждой строки, где-то просто список, где-то
// два числа и всё. Это ровно та беда, из-за которой заведено правило ритма: пока таких вещей
// две-три, разница незаметна, а на четвёртом экране продукт выглядит собранным из кусков.
//
// ## Вывод СЛОВАМИ стоит первым, картинка вторым
//
// Первый живой кадр показал полосу без единой подписи и таблицу с суммами без долей. Чтобы
// понять главное — «форма это три четверти всего долга» — надо было сложить пять сумм в уме и
// поделить. Полоса это показывала длиной, но длину нельзя прочитать вслух, а решение
// принимают по фразе, а не по ощущению от ширины.
//
// Поэтому под полосой стоит готовый вывод: «Больше всего — Форма: 1 452 600,00 ₽, это 76%».
// Полоса и таблица его подтверждают.
//
// ## Доля считается здесь, когда её не считает сервер
//
// Правило «доли приходят с сервера» запрещает ВТОРОЙ ответ на один вопрос: если сервер уже
// поделил, делить ещё раз на экране нельзя — округления разойдутся. Но там, где сервер долей
// не считает вовсе (долги, потери), деление на экране — единственный ответ, а не второй. И
// он нужен: без доли таблица заставляет складывать столбец в уме.
//
// ## Второстепенное отличается размером, а не бледностью
//
// В исходнике набора шапка таблицы и подписи набраны тоном `soft-400`. У нас он под текстом
// запрещён замером: 2,63:1 на белом при норме 4,5, а аудитория — люди с плохим зрением
// (`ui-views.md`, сторож `оформление.test.ts`).
//
// Вид взят готовым из набора AlignUI (`widget-visitor-channels`).

import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { formatRubles } from '@/shared/money/format';
import { CategoryBarChart } from '@/shared/ui/chart-category-bar';
import * as Badge from '@/shared/ui/badge';
import * as Divider from '@/shared/ui/divider';

/**
 * Цвета сегментов. Порядок закреплён: он же порядок легенды и порядок строк таблицы, и
 * человек связывает цвет со строкой ровно потому, что они идут одинаково.
 *
 * Шесть цветов, а не сколько получится: седьмой оттенок рядом с шестью соседями уже не
 * различается, а различать сегменты и есть вся работа полосы.
 */
const ЦВЕТА = [
  'bg-information-base',
  'bg-away-base',
  'bg-feature-base',
  'bg-verified-base',
  'bg-stable-base',
  'bg-highlighted-base',
] as const;

/** Хвост, свёрнутый в одну строку, красится серым: это не статья, а «всё остальное». */
const ЦВЕТ_ОСТАЛЬНЫХ = 'bg-faded-base';

export interface СтрокаСтруктуры {
  id: string;
  title: string;
  /**
   * Куда ведёт строка, если ей есть куда вести.
   *
   * Разбор суммы по ЛЮДЯМ отличается от разбора по статьям одним: за строкой стоит живой
   * человек, и следующий вопрос после «на ком висит больше всех» — «покажи его». Строка без
   * адреса остаётся обычным текстом: у статьи расхода карточки нет.
   */
  href?: string;
  /** Копейки. */
  amount: number;
  /** Доля в процентах, если её посчитал сервер. Нет — посчитается здесь. */
  percent?: number;
  /** Вторая величина строки: «2 заявки», «14 человек». Единица обязана быть в тексте. */
  note?: string;
  /** Пояснение под названием: «14 человек, 20 долгов». Из чего сложилась строка. */
  subtitle?: string;
}

export interface СтруктураСуммыProps {
  /** Что за сумма: «Принято за сентябрь 2026». */
  label: string;
  /** Итог в копейках. Из него считаются доли. */
  total: number;
  /**
   * Показывать ли итог крупным числом.
   *
   * Выключается там, где то же число уже стоит в ряду величин над блоком: два одинаковых
   * числа подряд человек читает как две разные величины и ищет между ними разницу.
   */
  showTotal?: boolean;
  /** Вторая величина под числом: «12 принятых заявок», «у 23 человек». С единицей в тексте. */
  footnote?: string;
  /** Строки. Порядок задаёт вызывающий — обычно по убыванию суммы. */
  rows: СтрокаСтруктуры[];
  /** Заголовок колонки с названием: «Статья», «Тип долга», «Причина». */
  nameHeader: string;
  /** Заголовок колонки со второй величиной. Без неё колонки нет. */
  noteHeader?: string;
  /** Правый верхний угол: переход в разрез, выгрузка. */
  action?: ReactNode;
  /** Метка рядом с числом: «+8% к прошлому месяцу». Текст и смысл задаёт вызывающий. */
  badge?: { text: string; tone: 'плохо' | 'хорошо' | 'ровно' };
  /** Сколько сегментов показать отдельно. Остальные сворачиваются в одну строку. */
  максимум?: number;
}

export function СтруктураСуммы({
  label,
  total,
  showTotal = true,
  footnote,
  rows,
  nameHeader,
  noteHeader,
  action,
  badge,
  максимум = ЦВЕТА.length,
}: СтруктураСуммыProps) {
  const { видимые, цвета } = свернутьХвост(rows, максимум, total);
  const самая = видимые.reduce<СтрокаСтруктуры | null>(
    (лучшая, строка) => (лучшая === null || строка.amount > лучшая.amount ? строка : лучшая),
    null,
  );
  // У единственной строки доля всегда «100%»: она не сравнивает ни с чем и занимает колонку
  // ради одного слова. Доля — величина относительная, и без второй строки смысла у неё нет.
  // Ровно по этой же причине при одной строке не рисуется ни полоса, ни вывод под ней.
  const естьДоля = видимые.length > 1;

  return (
    <div className='flex flex-col gap-4'>
      <div className='flex items-start gap-2'>
        <div className='flex-1'>
          <div className='text-paragraph-sm text-text-sub-600'>{label}</div>
          {showTotal && (
            <div className='mt-1 flex flex-wrap items-center gap-2'>
              <span className='text-title-h5 tabular-nums text-text-strong-950'>
                {formatRubles(total)}
              </span>
              {badge !== undefined && (
                <Badge.Root
                  variant='light'
                  color={
                    badge.tone === 'плохо' ? 'red' : badge.tone === 'хорошо' ? 'green' : 'gray'
                  }
                  size='medium'
                >
                  {badge.text}
                </Badge.Root>
              )}
            </div>
          )}
          {footnote !== undefined && (
            <div className='mt-1 text-paragraph-xs text-text-sub-600'>{footnote}</div>
          )}
        </div>
        {action !== undefined && <div className='shrink-0'>{action}</div>}
      </div>

      {/* Полоса рисуется, только когда сравнивать есть с чем. Единственная статья даёт всегда
          полную полосу и всегда «сто процентов»: она не сравнивает ни с чем и занимает место
          ради одного слова. Доля — величина относительная. */}
      {видимые.length > 1 && (
        <div className='flex flex-col gap-2'>
          <CategoryBarChart
            data={видимые.map((строка) => ({ label: строка.title, value: строка.amount }))}
            colors={цвета}
            // Легенды у полосы нет: цвет назван в таблице под ней, точкой у названия строки.
            // В исходнике набора легенда и таблица перечисляют РАЗНОЕ (легенда — три группы
            // каналов, таблица — площадки внутри них), у нас же список один и тот же, и
            // легенда повторяла бы его целиком — одно и то же дважды на одном экране.
            showLabels={false}
          />
          {/* Готовый вывод: то, ради чего на полосу и смотрят. Без него человек считает
              доли в уме, а картинка только намекает. */}
          {самая !== null && (
            <p className='text-paragraph-sm text-text-sub-600'>
              Больше всего — {самая.title}:{' '}
              <span className='tabular-nums text-text-strong-950'>
                {formatRubles(самая.amount)}
              </span>
              , это {самая.percent}% от всей суммы
            </p>
          )}
        </div>
      )}

      <Divider.Root variant='line-spacing' />

      <table className='w-full' cellPadding={0}>
        <thead className='text-left'>
          <tr>
            <th className='text-label-xs font-normal text-text-sub-600'>{nameHeader}</th>
            {естьДоля && (
              <th className='w-16 text-right text-label-xs font-normal text-text-sub-600'>Доля</th>
            )}
            {/* Колонка шире, чем кажется нужным: «Удержано за месяц» на 28 переносилось на
                две строки и роняло шапку таблицы на две высоты. */}
            {noteHeader !== undefined && (
              <th className='w-40 text-right text-label-xs font-normal text-text-sub-600'>
                {noteHeader}
              </th>
            )}
            <th className='w-36 text-right text-label-xs font-normal text-text-sub-600'>Сумма</th>
          </tr>
        </thead>
        {/* Пустое тело даёт зазор между шапкой и строками: у таблицы нет отступов, а рисовать
            их рамкой значит завести черту, которой в этом виде нет. Приём из набора. */}
        <tbody aria-hidden='true' className='h-3' />
        <tbody>
          {видимые.map((строка, номер) => (
            <tr key={строка.id}>
              <td className='py-1.5'>
                <span className='flex items-baseline gap-2 text-label-sm text-text-sub-600'>
                  <span
                    aria-hidden='true'
                    className={`size-2 shrink-0 translate-y-px rounded-full ${цвета[номер % цвета.length]}`}
                  />
                  <span className='flex min-w-0 flex-col'>
                    {строка.href === undefined ? (
                      <span className='truncate'>{строка.title}</span>
                    ) : (
                      <Link
                        to={строка.href}
                        className='truncate text-text-strong-950 hover:underline'
                      >
                        {строка.title}
                      </Link>
                    )}
                    {строка.subtitle !== undefined && (
                      <span className='truncate text-paragraph-xs text-text-sub-600'>
                        {строка.subtitle}
                      </span>
                    )}
                  </span>
                </span>
              </td>
              {естьДоля && (
                <td className='py-1.5 text-right text-label-sm tabular-nums text-text-sub-600'>
                  {строка.percent}%
                </td>
              )}
              {noteHeader !== undefined && (
                <td className='py-1.5 text-right text-paragraph-xs tabular-nums text-text-sub-600'>
                  {строка.note ?? ''}
                </td>
              )}
              <td className='py-1.5 text-right text-label-sm tabular-nums text-text-strong-950'>
                {/* Знак рубля есть: вплотную слева стоит величина ДРУГОЙ единицы («2 заявки»),
                    и без знака «2 заявки 4 650,00» читается как одно число из двух частей. */}
                {formatRubles(строка.amount)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Свернуть всё, что не влезло, в одну строку «ещё N», и проставить доли.
 *
 * Пятнадцать статей дают пятнадцать полосок по паре точек шириной: различить их нельзя, и
 * полоса перестаёт отвечать на свой вопрос. Хвост при этом не пропадает — он остаётся строкой
 * с суммой, иначе итог наверху перестал бы сходиться с таблицей под ним.
 *
 * Доля берётся С СЕРВЕРА, когда сервер её посчитал, и считается здесь, когда не посчитал.
 * Второго ответа на один вопрос это не заводит: в каждом отчёте делит кто-то ОДИН.
 */
function свернутьХвост(
  rows: СтрокаСтруктуры[],
  максимум: number,
  total: number,
): { видимые: СтрокаСтруктуры[]; цвета: string[] } {
  const сДолей = (строка: СтрокаСтруктуры): СтрокаСтруктуры => ({
    ...строка,
    percent: строка.percent ?? (total > 0 ? Math.round((строка.amount / total) * 100) : 0),
  });

  if (rows.length <= максимум) {
    return { видимые: rows.map(сДолей), цвета: [...ЦВЕТА] };
  }
  const головы = rows.slice(0, максимум - 1).map(сДолей);
  const хвост = rows.slice(максимум - 1).map(сДолей);
  const свёрнутый: СтрокаСтруктуры = {
    id: 'остальные',
    title: `Ещё ${хвост.length}`,
    amount: хвост.reduce((сумма, строка) => сумма + строка.amount, 0),
    // Складываем уже посчитанные доли, а не делим заново: сложение второго ответа не заводит.
    percent: хвост.reduce((доля, строка) => доля + (строка.percent ?? 0), 0),
  };
  return {
    видимые: [...головы, свёрнутый],
    цвета: [...ЦВЕТА.slice(0, максимум - 1), ЦВЕТ_ОСТАЛЬНЫХ],
  };
}
